import { ActionIcon, Box, Menu, createStyles } from "@mantine/core";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { IconEye, IconPencil, IconTrash } from "@tabler/icons-react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import { deleteNoteDraft } from "../../services/note";
import { setDrafts, setNotes } from "../../store/reducers/note";

const useStyles = createStyles(() => ({
  bg1: {
    "& td": {
      background: "var(--app-surface) !important",
      borderBottom: "1px solid var(--app-border) !important",
      padding: "13px 16px !important",
      fontSize: "13.5px !important",
    },
    cursor: "pointer",
  },
  bg2: {
    "& td": {
      background: "var(--app-surface) !important",
      borderBottom: "1px solid var(--app-border) !important",
      padding: "13px 16px !important",
      fontSize: "13.5px !important",
    },
    cursor: "pointer",
  },
  textDark: {
    "&&": { color: "var(--app-text) !important" },
  },
  textMuted: {
    "&&": { color: "var(--app-text-muted) !important" },
  },
  draftBadge: {
    display: "inline-block",
    marginLeft: 8,
    padding: "1px 7px",
    borderRadius: 999,
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    background: "var(--app-primary-soft, #FFF6E9)",
    color: "#B4700A",
    border: "1px solid var(--app-primary-bd, #FFE2B8)",
  },
  kebab: {
    "&&": {
      width: "30px !important",
      height: "30px !important",
      minHeight: "30px !important",
      minWidth: "30px !important",
      borderRadius: "8px !important",
      border: "1px solid transparent !important",
      background: "none !important",
      color: "var(--app-text-faint, #8A93A2) !important",
      cursor: "pointer",
      display: "grid",
      placeItems: "center",
      "&:hover": {
        background: "var(--app-surface-2) !important",
        borderColor: "var(--app-border) !important",
        color: "var(--app-text) !important",
      },
    },
  },
}));

// Renders one draft as a row inside the same table as submitted notes (the
// "All" tab merges drafts in — see note.service.js's getNotes `mergeDrafts`
// path). Kept separate from NoteTableRow instead of branching inside it: a
// draft's shape (formState.fields, resolved client/staff/facility objects)
// has nothing in common with a real Note's (fields ids + noteId.custom), so
// reusing NoteTableRow's field-resolution logic would mean threading a
// second, incompatible data shape through it.
//
// Columns match NoteTable exactly (Id/Note Type/Staff/Date/Time/Facility/
// Client/Description/Actions) — Date/Time/Description pull straight from
// formState.fields (same field keys the eventual submitted note would use),
// falling back to "-" for whatever the staff hasn't filled in yet. Client
// Name/Staff Name/Facility Name and the Continue Editing/Delete actions are
// the same as the dedicated Drafts tab (DraftNotesTab).
const DraftTableRow = ({ row: draft, index, onEditDraft, onViewDraft, onDeleted }) => {
  const dispatch = useDispatch();
  const { drafts, notes } = useSelector((state) => state.note);
  const { allStaff } = useSelector((state) => state?.staff);
  const { classes } = useStyles();

  const bg = index % 2 === 0;
  const firstClient = Array.isArray(draft.client)
    ? draft.client[0]
    : draft.client;
  const fields = draft.formState?.fields || {};

  // Clicking a draft row opens the read-only view drawer (shown with a "Draft"
  // status), the same as clicking a submitted note opens its view. Editing the
  // draft is still available via the "Continue Editing" menu action below.
  const handleRowClick = () => onViewDraft?.(draft);

  const getClientName = () => {
    if (Array.isArray(draft.client)) {
      const names = draft.client.map((c) => c?.name).filter(Boolean);
      return names.length ? names.join(", ") : "-";
    }
    return draft.client?.name || "-";
  };

  // Multi-staff selections (Incident Report's staffInvolved, or the newer
  // multiStaffIds for Essential Support/Billing Services/Intervention
  // Progress/Progress Note) live in formState — not the fields["Staff Name"]
  // scalar, which is only ever populated at actual Save time — so a draft
  // saved with multiple staff selected must resolve them from there too.
  const getStaffName = () => {
    const multiIds =
      draft.formState?.multiStaffIds?.length
        ? draft.formState.multiStaffIds
        : draft.formState?.incidentDetails?.staffInvolved?.length
        ? draft.formState.incidentDetails.staffInvolved
        : null;
    if (multiIds?.length) {
      const names = multiIds
        .map((id) => allStaff?.find((s) => s?.id === id || s?._id === id)?.name)
        .filter(Boolean);
      if (names.length) return names.join(", ");
    }
    const staffId = fields["Staff Name"];
    if (staffId) {
      const staff = allStaff?.find(
        (s) => s?.id === staffId || s?._id === staffId,
      );
      if (staff?.name) return staff.name;
    }
    return draft.staff?.name || "-";
  };

  const handleDelete = async () => {
    const confirmed = await swal({
      title: "Delete Draft?",
      text: "This draft will be permanently deleted and cannot be recovered.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (!confirmed) return;

    await deleteNoteDraft(
      draft.noteType?._id || draft.noteType,
      firstClient?._id || firstClient,
    );
    // Keeps the Drafts tab's own count badge correct immediately.
    dispatch(setDrafts((drafts || []).filter((d) => d._id !== draft._id)));
    // This row itself comes from the "All" tab's `getNotes` response, not
    // the `drafts` slice above — same convention NoteTableRow's own delete
    // uses for real notes, re-fetch from the server instead of splicing.
    onDeleted?.();
  };

  return (
    <tr className={bg ? classes.bg2 : classes.bg1}>
      <td className={classes.textMuted} onClick={handleRowClick} style={{ textTransform: "uppercase" }}>
        {draft._id?.slice(-6)}
        {/* <Box component="span" className={classes.draftBadge}>
          Draft
        </Box> */}
      </td>
      <td className={classes.textDark} onClick={handleRowClick}>{draft.noteType?.name || "-"}</td>
      <td className={classes.textMuted} onClick={handleRowClick}>{getStaffName()}</td>
      <td className={classes.textMuted} onClick={handleRowClick}>
        {fields["Date"] && moment(fields["Date"]).isValid()
          ? moment(fields["Date"]).format("MM-DD-YYYY")
          : "-"}
      </td>
      <td className={classes.textMuted} onClick={handleRowClick}>
        {fields["Time"] && moment(fields["Time"], "HH:mm:ss").isValid()
          ? moment(fields["Time"], "HH:mm:ss").format("hh:mm:ss a")
          : "-"}
      </td>
      <td className={classes.textMuted} onClick={handleRowClick}>{draft.facility?.name || "-"}</td>
      <td className={classes.textDark} onClick={handleRowClick}>{getClientName()}</td>
      <td className={classes.textMuted} onClick={handleRowClick}>
        {fields["Description"] && fields["Description"].trim() !== ""
          ? fields["Description"].length > 140
            ? `${fields["Description"].slice(0, 140)}...`
            : fields["Description"]
          : "-"}
      </td>
      <td>
        <Menu shadow="md" position="bottom-end" withinPortal zIndex={1500}>
          <Menu.Target>
            <ActionIcon className={classes.kebab}>
              <BiDotsVerticalRounded size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item icon={<IconEye size={16} />} onClick={handleRowClick}>
              View
            </Menu.Item>
            <Menu.Item icon={<IconPencil size={16} />} onClick={() => onEditDraft?.(draft)}>
              Continue Editing
            </Menu.Item>
            <Menu.Item color="red" icon={<IconTrash size={16} />} onClick={handleDelete}>
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </td>
    </tr>
  );
};

export default DraftTableRow;
